import {Component, computed, effect, inject, input, output, signal} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {Button} from 'primeng/button';
import {ProjectDecisionControllerService} from '@frontend/shared';
import {AiProvenanceView} from '@frontend/shared-api';
import {ToastService} from '@frontend/toast';
import {ModelFieldTextareaComponent} from '../../../../../../../../../shared/components/inputs/model-field/model-field-textarea/model-field-textarea.component';
import {ProjectStepSaveButtonComponent} from '../../../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {AiGeneratedLabelComponent} from '../../../../../../../../../shared/components/ai-generated-label/ai-generated-label.component';

@Component({
  selector: 'app-decision-reason-field',
  imports: [
    TranslocoPipe,
    Button,
    ModelFieldTextareaComponent,
    ProjectStepSaveButtonComponent,
    AiGeneratedLabelComponent,
  ],
  templateUrl: './decision-reason-field.component.html',
})
export class DecisionReasonFieldComponent {
  private readonly projectDecisionController = inject(ProjectDecisionControllerService);
  private readonly toast = inject(ToastService);

  projectId = input.required<number>();
  hrToolDirectusId = input.required<string>();
  initialReasonText = input.required<string>();
  isReadOnly = input<boolean>(false);

  reasonSatisfied = output<boolean>();

  protected readonly reasonText = signal<string>('');
  private readonly savedReasonText = signal<string>('');
  protected readonly isSaving = signal(false);
  protected readonly isImproving = signal(false);
  protected readonly reasonProvenance = signal<AiProvenanceView | undefined>(undefined);

  protected readonly hasReasonText = computed(() => this.reasonText().trim().length > 0);
  protected readonly hasUnsavedChanges = computed(
    () =>
      !this.isReadOnly() &&
      this.hasReasonText() &&
      this.reasonText() !== this.savedReasonText() &&
      !this.isImproving(),
  );

  constructor() {
    effect(() => {
      this.applyReasonText(this.initialReasonText());
    });

    effect(() => {
      this.reasonSatisfied.emit(this.hasReasonText());
    });
  }

  protected onReasonTextChange(value: string | null | undefined) {
    this.reasonText.set(value ?? '');
  }

  protected saveReason() {
    this.isSaving.set(true);
    this.projectDecisionController
      .upsertDecisionReason({
        projectId: this.projectId(),
        decisionReasonUpsertRequest: {
          hrToolDirectusId: this.hrToolDirectusId(),
          reasonText: this.reasonText(),
        },
      })
      .subscribe({
        next: (response) => {
          this.applyReasonText(response.reasonText ?? '');
          this.reasonProvenance.set(response.aiProvenance);
          void this.toast.showSuccessMessage();
          this.isSaving.set(false);
        },
        error: () => this.isSaving.set(false),
      });
  }

  protected improveReason() {
    this.isImproving.set(true);
    this.projectDecisionController
      .reformulateDecisionReason({
        projectId: this.projectId(),
        decisionReasonReformulateRequest: {
          hrToolDirectusId: this.hrToolDirectusId(),
          currentText: this.reasonText(),
        },
      })
      .subscribe({
        next: (response) => {
          this.applyReasonText(response.reasonText ?? '');
          this.reasonProvenance.set(response.aiProvenance);
          this.isImproving.set(false);
        },
        error: () => this.isImproving.set(false),
      });
  }

  private applyReasonText(text: string) {
    this.reasonText.set(text);
    this.savedReasonText.set(text);
  }
}
